/**
 * TVView - Universal TV display for HTML content from GameModeSDK.
 *
 * ARCHITECTURE: View component (src/views/)
 * - NO Socket.io connection management (done by parent Page)
 * - Uses hooks (useGameDisplay) that assume socket is already connected
 * - Reads data from stores (gameStore.tvRegions)
 * - Renders HTML emitted by backend GameModeSDK
 *
 * Parent: GamePage (src/pages/GamePage.tsx) calls useSocket()
 *
 * PHASE 8: Renders HTML regions emitted by backend GameMode:
 * - header: Top bar (game title, info)
 * - sidebar: Left sidebar (metadata)
 * - main: Main content area (game display)
 * - footer: Bottom bar (stats, timer)
 * - fullscreen: Overlay for important messages (winners, announcements)
 *
 * All HTML is sanitized with DOMPurify before rendering.
 */

import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useGameStore } from '@/stores/gameStore'
import type { TVSidebarWidth } from '@/stores/gameStore'
import { usePlayerStore } from '@/stores/playerStore'
import { useGameDisplay } from '@/hooks/useGameDisplay'
import { useGameLifecycle } from '@/hooks/useGameLifecycle'
import { useAudio } from '@/hooks/useAudio'
import { useTtsCaptions } from '@/hooks/useTtsCaptions'
import { useAnimatedEmoji } from '@/hooks/useAnimatedEmoji'
import { AudioIndicator } from '@/components/high/AudioIndicator'
import { LoadingOverlay } from '@/components/low/LoadingOverlay'
import DOMPurify from 'dompurify'

/** Sidebar container width per size requested by the game mode (192 / 320 / 448 px). */
const SIDEBAR_WIDTH_CLASS: Record<TVSidebarWidth, string> = {
  narrow: 'w-48',
  standard: 'w-80',
  wide: 'w-[28rem]',
}

/**
 * TVView component.
 *
 * Cinematic HUD Layout:
 * - Session HUD (in flow, flex-shrink-0)
 * - Dynamic Regions (Header, Main+Sidebar, Footer)
 * - Fullscreen Overlay
 */
export function TVView() {
  const { t } = useTranslation()
  useGameDisplay()
  useGameLifecycle()
  useAudio()
  useTtsCaptions()

  const tvRegions = useGameStore((state) => state.tvRegions)
  const tvRegionsUnsafe = useGameStore((state) => state.tvRegionsUnsafe)
  const tvSidebarWidth = useGameStore((state) => state.tvSidebarWidth)
  const gameModeTitle = useGameStore((state) => state.gameModeTitle)
  const loadingOverlay = useGameStore((state) => state.loadingOverlay)
  const sessionCode = usePlayerStore((state) => state.sessionCode)

  // Refs for executing scripts in unsafe HTML
  const mainRef = useRef<HTMLDivElement>(null)
  const headerRef = useRef<HTMLDivElement>(null)
  const sidebarRef = useRef<HTMLDivElement>(null)
  const footerRef = useRef<HTMLDivElement>(null)
  const fullscreenRef = useRef<HTMLDivElement>(null)

  // Animate emoji in sanitized regions. Unsafe regions are excluded: their scripts inspect
  // textContent and selectors, which the emoji replacement would change under them.
  const animatable = (html: string, unsafe: boolean): string | null => (unsafe ? null : html)
  useAnimatedEmoji(headerRef, animatable(tvRegions.header, tvRegionsUnsafe.header))
  useAnimatedEmoji(sidebarRef, animatable(tvRegions.sidebar, tvRegionsUnsafe.sidebar))
  useAnimatedEmoji(mainRef, animatable(tvRegions.main, tvRegionsUnsafe.main))
  useAnimatedEmoji(footerRef, animatable(tvRegions.footer, tvRegionsUnsafe.footer))
  useAnimatedEmoji(fullscreenRef, animatable(tvRegions.fullscreen, tvRegionsUnsafe.fullscreen))

  // Execute scripts after HTML is inserted (React doesn't execute <script> in dangerouslySetInnerHTML)
  useEffect(() => {
    const executeScripts = (container: HTMLDivElement | null, unsafe: boolean) => {
      if (!container || !unsafe) return

      const timeoutId = setTimeout(() => {
        const scripts = container.querySelectorAll('script')
        scripts.forEach((oldScript) => {
          const newScript = document.createElement('script')
          Array.from(oldScript.attributes).forEach((attr) =>
            newScript.setAttribute(attr.name, attr.value)
          )
          newScript.textContent = oldScript.textContent
          oldScript.parentNode?.replaceChild(newScript, oldScript)
        })
      }, 0)

      return () => clearTimeout(timeoutId)
    }

    executeScripts(mainRef.current, tvRegionsUnsafe.main)
    executeScripts(headerRef.current, tvRegionsUnsafe.header)
    executeScripts(sidebarRef.current, tvRegionsUnsafe.sidebar)
    executeScripts(footerRef.current, tvRegionsUnsafe.footer)
    executeScripts(fullscreenRef.current, tvRegionsUnsafe.fullscreen)
  }, [
    tvRegions.main,
    tvRegions.header,
    tvRegions.sidebar,
    tvRegions.footer,
    tvRegions.fullscreen,
    tvRegionsUnsafe.main,
    tvRegionsUnsafe.header,
    tvRegionsUnsafe.sidebar,
    tvRegionsUnsafe.footer,
    tvRegionsUnsafe.fullscreen,
  ])

  return (
    <div className="h-screen bg-background text-foreground flex flex-col overflow-hidden relative font-sans selection:bg-primary/30">
      {/* Session HUD (in flow) */}
      <div className="flex-shrink-0 flex justify-center pt-3 pb-2 relative z-30 pointer-events-none">
        <div className="bg-card px-8 py-1.5 pixel-frame flex items-center gap-6">
          {/* 10-foot UI: the arcade face is kept off this screen entirely and
              label sizes stay well above the phone-sized originals, because a
              pixel face below ~12 px is unreadable from a sofa. */}
          <div className="flex flex-col items-center">
            <span className="font-display text-xs text-muted-foreground leading-none mb-1">{t('join.sessionCodeLabel')}</span>
            <span className="font-mono text-xl font-bold tracking-[0.2em] text-secondary">
              {sessionCode || '--- ---'}
            </span>
          </div>
          <div className="h-8 w-[2px] bg-border" />
          <div className="flex flex-col items-start">
            <span className="font-display text-xs text-muted-foreground leading-none mb-1">{t('tv.currentGame')}</span>
            <div className="flex items-center gap-2">
              <span className="font-giermek text-sm font-bold tracking-wide text-foreground">{gameModeTitle}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Header Region (Optional SDK Content) */}
      {tvRegions.header && (
        <div className="flex-shrink-0 bg-card/60 backdrop-blur-sm border-b border-border/30 px-6 py-2 relative z-10">
          <div
            ref={headerRef}
            className="prose-invert prose-2xl max-w-none text-center"
            dangerouslySetInnerHTML={{
              __html: tvRegionsUnsafe.header
                ? tvRegions.header
                : DOMPurify.sanitize(tvRegions.header),
            }}
          />
        </div>
      )}

      {/* Main Content with Sidebar */}
      <div className="flex-grow flex overflow-hidden relative z-10">
        {/* Sidebar Region */}
        {tvRegions.sidebar && (
          <div className={`${SIDEBAR_WIDTH_CLASS[tvSidebarWidth]} max-w-[33vw] flex-shrink-0 bg-card/40 backdrop-blur-md border-r border-border/30 p-4 overflow-y-auto custom-scrollbar`}>
            <div
              ref={sidebarRef}
              className="prose-invert prose-xl max-w-none"
              dangerouslySetInnerHTML={{
                __html: tvRegionsUnsafe.sidebar
                  ? tvRegions.sidebar
                  : DOMPurify.sanitize(tvRegions.sidebar),
              }}
            />
          </div>
        )}

        {/* Main Region */}
        <div className="flex-grow flex flex-col items-center justify-center p-3 relative overflow-hidden">
          {tvRegions.main ? (
            <div
              ref={mainRef}
              className="prose-invert prose-2xl max-w-none w-full h-full flex flex-col overflow-hidden"
              dangerouslySetInnerHTML={{
                __html: tvRegionsUnsafe.main
                  ? tvRegions.main
                  : DOMPurify.sanitize(tvRegions.main),
              }}
            />
          ) : (
            <div className="flex flex-col items-center gap-6 opacity-20">
              <div className="w-24 h-24 border-4 border-border border-t-primary rounded-full animate-spin" />
              <span className="text-xl text-muted-foreground">{t('tv.synchronizing')}</span>
            </div>
          )}
        </div>
      </div>

      {/* Footer Region */}
      {tvRegions.footer && (
        <div className="flex-shrink-0 bg-card/60 backdrop-blur-sm border-t border-border/30 px-6 py-2 relative z-10">
          <div
            ref={footerRef}
            className="prose-invert prose-xl max-w-none text-center"
            dangerouslySetInnerHTML={{
              __html: tvRegionsUnsafe.footer
                ? tvRegions.footer
                : DOMPurify.sanitize(tvRegions.footer),
            }}
          />
        </div>
      )}

       {/* Fullscreen Overlay */}
       {tvRegions.fullscreen && (
         <div className="absolute inset-0 z-50 flex items-center justify-center bg-background/95 backdrop-blur-lg">
           <div className="max-w-6xl w-full px-12">
             <div
               ref={fullscreenRef}
               className="prose-invert prose-3xl max-w-none text-center"
               dangerouslySetInnerHTML={{
                 __html: tvRegionsUnsafe.fullscreen
                   ? tvRegions.fullscreen
                   : DOMPurify.sanitize(tvRegions.fullscreen),
               }}
             />
           </div>
         </div>
       )}

        {/* Audio Indicator */}
        <AudioIndicator variant="tv" />

        {/* Loading Overlay */}
        {loadingOverlay?.visible && (
          <LoadingOverlay messages={loadingOverlay.messages} />
        )}
      </div>
    )
  }
